import React, { useEffect, useMemo, useState } from "react";
import { X, Loader2, Minus, Plus, Undo2, Check, Tag } from "lucide-react";
import { toLocalISODate } from "../../utils/date";
import { effectiveNightlyRate, hotelDateStr } from "../../utils/pricing";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
// Written on every rate set from this panel, so the panel can recognise
// its own edits and offer to undo them — and never offer to undo a season.
// "Calendar price" is what this panel wrote before it was renamed; rates
// already saved under it are still its own.
const SPECIAL_LABEL = "Special rate";
const isSpecial = season => season.label === SPECIAL_LABEL || season.label === "Calendar price";

function addDays(ymd, n) {
  const [y, m, d] = ymd.split("-").map(Number);
  return toLocalISODate(new Date(y, m - 1, d + n));
}
const pretty = ymd => {
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" });
};
const money = n => `₹${Math.round(n || 0).toLocaleString("en-IN")}`;

/** The entry that sets this night's price — the same first-match rule as
    effectiveNightlyRate on both frontend and backend. */
function priceSource(room, date) {
  for (const season of room.rateOverrides || []) {
    const range = (season.dateRanges || []).find(r =>
      date >= hotelDateStr(r.startDate) && date <= hotelDateStr(r.endDate));
    if (range) return { season, range };
  }
  return null;
}

/* Every room's price for one night, editable in place.

   Opened by tapping a day in the calendar's Rates view. A change is saved
   as a price for exactly the nights chosen (one by default), placed ahead of
   any season, so a season covering those nights is left intact for all its
   other nights. Seasons are shown, never edited here — a tap on one day must
   not be able to reshape a ten-day season. What this panel set, it can undo,
   which brings back whatever priced the night before.

   Goes through POST /rates/bulk: one request for every room changed, which
   the channel manager's outbox pushes as a single rate update for exactly
   those nights.

   Opened for a run of nights (a drag across days) the stepper starts there,
   and each room says when its price differs from night to night, since the
   one figure typed replaces all of them. roomId narrows the panel to one room
   type — the list view opens it from that type's own rate row. */
export default function DayRatesSheet({ date, initialNights = 1, roomId = null, authHeaders, onClose, onChanged }) {
  const [rooms, setRooms] = useState(null);
  const [prices, setPrices] = useState({});
  const [nights, setNights] = useState(initialNights);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState("");

  async function loadRooms() {
    try {
      const j = await fetch(`${API}/rooms`, { headers: authHeaders() }).then(r => r.json());
      const list = (j.data || []).filter(r => r.active !== false && (!roomId || String(r._id) === String(roomId)));
      setRooms(list);
      setPrices(Object.fromEntries(list.map(r => [r._id, String(effectiveNightlyRate(r, date))])));
    } catch { setRooms([]); }
  }
  useEffect(() => { loadRooms(); }, []);

  const rows = useMemo(() => (rooms || []).map(room => {
    const current = effectiveNightlyRate(room, date);
    const src = priceSource(room, date);
    const value = prices[room._id] ?? String(current);
    const n = Number(value);
    // Every night's price across the run, so a value that matches the first
    // night still counts as a change when later nights differ.
    const all = Array.from({ length: nights }, (_, i) => effectiveNightlyRate(room, addDays(date, i)));
    const lo = Math.min(...all), hi = Math.max(...all);
    return { room, current, src, value, lo, hi, valid: Number.isFinite(n) && n > 0,
      changed: value !== "" && (n !== current || lo !== hi) };
  }), [rooms, prices, date, nights]);

  const changes = rows.filter(r => r.changed && r.valid);
  const invalid = rows.some(r => r.changed && !r.valid);
  const lastNight = addDays(date, nights - 1);
  const span = nights > 1 ? `${pretty(date)} → ${pretty(lastNight)}` : pretty(date);

  async function save() {
    setError(""); setSaved("");
    // A price an OTA will publish must not be a slip of the finger: Booking.com
    // rejects anything under about ₹80 outright, and far below a room's
    // standard price is more often a missing digit than a decision.
    const odd = changes.filter(r => Number(r.value) < 100 || Number(r.value) < (r.room.pricePerNight || 0) * 0.4);
    if (odd.length && !window.confirm(
      `${odd.map(r => `${r.room.name}: ${money(r.value)}`).join(", ")} — much lower than usual. This price goes to every connected OTA. Save anyway?`)) return;

    setBusy("save");
    try {
      const res = await fetch(`${API}/rates/bulk`, {
        method: "POST", headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ changes: changes.map(r => ({
          roomIds: [r.room._id], startDate: date, endDate: lastNight, price: Number(r.value), label: SPECIAL_LABEL,
        })) }),
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok || j.success === false) throw new Error(j.error || `Couldn't save (${res.status})`);
      setSaved(`Saved ${changes.length} price${changes.length > 1 ? "s" : ""} for ${nights > 1 ? `${pretty(date)} → ${pretty(lastNight)}` : pretty(date)}.`);
      await loadRooms();
      await onChanged();
    } catch (err) { setError(err.message); }
    finally { setBusy(null); }
  }

  async function undo(room, src) {
    const span = hotelDateStr(src.range.startDate) === hotelDateStr(src.range.endDate)
      ? pretty(hotelDateStr(src.range.startDate))
      : `${pretty(hotelDateStr(src.range.startDate))} → ${pretty(hotelDateStr(src.range.endDate))}`;
    if (!window.confirm(`Remove the special rate for ${room.name} on ${span}? Those nights go back to the price they had before.`)) return;
    setBusy(room._id); setError(""); setSaved("");
    try {
      const res = await fetch(`${API}/rooms/${room._id}/rate-overrides/${src.season._id}/date-ranges/${src.range._id}`,
        { method: "DELETE", headers: authHeaders() });
      const j = await res.json().catch(() => ({}));
      if (!res.ok || j.success === false) throw new Error(j.error || `Couldn't undo (${res.status})`);
      await loadRooms();
      await onChanged();
    } catch (err) { setError(err.message); }
    finally { setBusy(null); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm sm:p-4"
      role="dialog" aria-modal="true" aria-label={`Prices on ${span}`} onClick={onClose}>
      <div className="bg-white w-full sm:max-w-lg rounded-t-3xl sm:rounded-2xl shadow-2xl max-h-[88vh] flex flex-col"
        onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 px-5 pt-4 pb-2">
          <div>
            <h3 className="font-semibold text-gray-900">Prices · {span}</h3>
            <p className="text-xs text-gray-500">Per room, per night. Changes go to every connected OTA.</p>
          </div>
          <button onClick={onClose} aria-label="Close" className="shrink-0 w-11 h-11 -mr-2 flex items-center justify-center rounded-xl hover:bg-gray-100">
            <X size={18} className="text-gray-400" />
          </button>
        </div>

        <div className="px-5 pb-3 border-b border-gray-100">
          <span className="block text-[11px] font-semibold text-gray-500 mb-1">Apply to</span>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center rounded-xl border border-gray-200">
              <button type="button" onClick={() => setNights(n => Math.max(1, n - 1))} aria-label="Fewer nights"
                className="w-11 h-11 flex items-center justify-center text-gray-500 hover:bg-gray-50 rounded-l-xl"><Minus size={15} /></button>
              <span className="min-w-[64px] text-center text-sm font-semibold text-gray-900 tabular-nums">{nights} night{nights > 1 ? "s" : ""}</span>
              <button type="button" onClick={() => setNights(n => Math.min(90, n + 1))} aria-label="More nights"
                className="w-11 h-11 flex items-center justify-center text-gray-500 hover:bg-gray-50 rounded-r-xl"><Plus size={15} /></button>
            </div>
            <span className="text-[11px] text-gray-400">{nights > 1 ? `${pretty(date)} → ${pretty(lastNight)}` : pretty(date)}</span>
          </div>
        </div>

        <div className="overflow-y-auto px-5 py-3 space-y-2.5 flex-1">
          {error && <p className="text-xs rounded-xl px-3 py-2.5 bg-rose-50 text-rose-700">{error}</p>}
          {saved && <p className="text-xs rounded-xl px-3 py-2.5 bg-emerald-50 text-emerald-700">{saved}</p>}
          {!rooms && <div className="flex justify-center py-8"><Loader2 className="animate-spin text-amber-500" /></div>}

          {rows.map(({ room, current, src, value, lo, hi, valid, changed }) => {
            const isCalendar = src && isSpecial(src.season);
            return (
              <div key={room._id} className={`rounded-xl border px-3 py-3 ${changed ? "border-amber-300 bg-amber-50/40" : "border-gray-100"}`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">{room.name}</p>
                    <p className="text-[11px] text-gray-500 flex items-center gap-1 flex-wrap">
                      {!src && <>Standard rate</>}
                      {src && isCalendar && <><Tag size={10} /> Special rate</>}
                      {src && !isCalendar && <><Tag size={10} /> Seasonal rate{src.season.label ? `: ${src.season.label}` : ""} ({pretty(hotelDateStr(src.range.startDate))}
                        {hotelDateStr(src.range.startDate) !== hotelDateStr(src.range.endDate) && ` → ${pretty(hotelDateStr(src.range.endDate))}`})</>}
                      <span className="text-gray-400">· {lo !== hi ? `now ${money(lo)}–${money(hi)} over these nights` : `now ${money(current)}`}</span>
                    </p>
                  </div>
                  <label className="shrink-0 relative">
                    <span className="sr-only">Price for {room.name}</span>
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-400">₹</span>
                    <input type="number" inputMode="numeric" min="1" step="1" value={value}
                      onChange={e => setPrices(p => ({ ...p, [room._id]: e.target.value }))}
                      className={`w-28 min-h-[44px] pl-7 pr-2 text-sm font-semibold tabular-nums rounded-xl border bg-white
                        focus:outline-none focus:ring-2 focus:ring-amber-400 ${changed && !valid ? "border-rose-400" : "border-gray-200"}`} />
                  </label>
                </div>
                {isCalendar && (
                  <button onClick={() => undo(room, src)} disabled={!!busy}
                    className="mt-2 inline-flex items-center gap-1.5 min-h-[44px] px-3 text-xs font-medium rounded-xl border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                    {busy === room._id ? <Loader2 size={14} className="animate-spin" /> : <Undo2 size={14} />} Undo special rate
                  </button>
                )}
              </div>
            );
          })}
          {rooms && !rows.length && <p className="text-sm text-gray-400 text-center py-6">No rooms set up yet.</p>}
          {rooms && rows.some(r => r.src && !isSpecial(r.src.season)) && (
            <p className="text-[11px] text-gray-400">A special rate here overrides the seasonal rate for just the nights you choose — the season's other nights keep their rate. Seasonal rates themselves are edited on the Rates page.</p>
          )}
        </div>

        <div className="px-5 pt-3 pb-[max(1rem,env(safe-area-inset-bottom))] border-t border-gray-100">
          <button onClick={save} disabled={!changes.length || invalid || !!busy}
            className="w-full inline-flex items-center justify-center gap-1.5 min-h-[48px] text-sm font-semibold rounded-xl text-white
              bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 disabled:opacity-40">
            {busy === "save" ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
            {invalid ? "Enter a price above ₹0" : changes.length ? `Save ${changes.length} price${changes.length > 1 ? "s" : ""}` : "No changes"}
          </button>
        </div>
      </div>
    </div>
  );
}
